import { useEffect, useState } from "react";
import { toast } from "sonner";

import BottomMobileOptionsModal from "@/components/bottom-mobile-options-modal";
import SignupModal from "@/components/signup-modal";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useReportContent, {
  ReportContentTypeEnum,
  type ReportContentViolationEnum,
} from "@/graphql/mutations/use-report-content";
import useAuth from "@/hooks/use-auth";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";

import type { GameType, ReviewType } from "../types";
import { MoreIcon } from "./icons";
import MenuItems from "./menu-items";
import ReportToast from "./report-toast";

const REPORT_STORAGE_KEY = "report_review";

interface OptionsMenuProps {
  className?: string;
  game: GameType;
  review: ReviewType;
}

export default function OptionsMenu({
  className,
  game,
  review,
}: OptionsMenuProps) {
  const { isUser, mutationContext } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);

  const isDesktop = useIsBreakPoint("md");
  const [reportContent] = useReportContent();

  const signupAndReport = (violation: ReportContentViolationEnum) => {
    setIsSignupOpen(true);
    sessionStorage.setItem(
      REPORT_STORAGE_KEY,
      JSON.stringify({ id: review.id, violation })
    );
  };

  const reportViolation = (violation: ReportContentViolationEnum) => {
    toast.custom((id) => <ReportToast onClose={() => toast.dismiss(id)} />);
    reportContent(
      mutationContext({
        id: review.id,
        type: ReportContentTypeEnum.REVIEW,
        violation,
      })
    );
  };

  const handleCancelReport = (open: boolean) => {
    setIsSignupOpen(open);
    sessionStorage.removeItem(REPORT_STORAGE_KEY);
  };

  const handleReport = (violation: ReportContentViolationEnum) => () => {
    if (isUser) {
      reportViolation(violation);
    } else {
      signupAndReport(violation);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: its fine
  useEffect(() => {
    if (!isUser) {
      return;
    }

    const stored = JSON.parse(
      sessionStorage.getItem(REPORT_STORAGE_KEY) || "null"
    ) as null | {
      id: string;
      violation: ReportContentViolationEnum;
    };

    if (!stored || stored.id !== review.id) {
      return;
    }

    reportViolation(stored.violation);
    sessionStorage.removeItem(REPORT_STORAGE_KEY);
  }, [isUser, review.id]);

  return (
    <>
      <Popover onOpenChange={setIsOpen} open={isOpen}>
        <PopoverTrigger aria-label="More options" className={className}>
          <MoreIcon className="mx-auto" />
        </PopoverTrigger>
        {isDesktop && (
          <PopoverContent
            align="start"
            className="w-auto rounded-[15px] bg-twilight-indigo p-2.5 shadow-0-0-8 shadow-black/25"
          >
            <MenuItems game={game} onReport={handleReport} review={review} />
          </PopoverContent>
        )}
      </Popover>
      {!isDesktop && (
        <BottomMobileOptionsModal onOpenChange={setIsOpen} open={isOpen}>
          <MenuItems game={game} onReport={handleReport} review={review} />
        </BottomMobileOptionsModal>
      )}
      <SignupModal onOpenChange={handleCancelReport} open={isSignupOpen} />
    </>
  );
}
